import { useEffect, useRef, useState, useCallback } from 'react';
import confetti from 'canvas-confetti';

const CelebrationBlast = ({ isOpen = false, duration = 6000, title = '', onComplete }) => {
  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | active | fading
  const [titleVisible, setTitleVisible] = useState(false);

  const canvasRef = useRef(null);
  const cleanupRef = useRef(null);
  const onCompleteRef = useRef(onComplete);

  const FADE_DURATION = 2500;
  const TITLE_FADE_DURATION = 800;

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const cleanup = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
  }, []);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTitleVisible(true);
      setPhase('active');
    } else {
      cleanup();
      setPhase('idle');
      setVisible(false);
      setTitleVisible(false);
    }
  }, [isOpen, cleanup]);

  useEffect(() => {
    if (!visible || phase !== 'active' || !canvasRef.current) return;

    const totalDuration = Math.max(1000, duration);

    const blast = confetti.create(canvasRef.current, {
      resize: true,
      useWorker: true,
    });

    const colors = [
      '#FFC700',
      '#FF6B6B',
      '#6C5CE7',
      '#00D1FF',
      '#2ED573',
      '#FF8C42',
      '#FF4081',
      '#7C4DFF',
      '#18FFFF',
      '#FFEA00',
    ];

    const fire = (particleCount, options = {}) => {
      blast({
        particleCount,
        spread: 70,
        startVelocity: 50,
        gravity: 0.8,
        ticks: 300,
        scalar: 1.1,
        colors,
        disableForReducedMotion: true,
        ...options,
      });
    };

    // Opening celebration burst
    fire(180, { angle: 55, origin: { x: 0, y: 0.7 } });
    fire(180, { angle: 125, origin: { x: 1, y: 0.7 } });
    fire(150, { angle: 90, spread: 120, origin: { x: 0.5, y: 0 } });
    fire(100, {
      spread: 360,
      startVelocity: 40,
      scalar: 1.3,
      origin: { x: 0.5, y: 0.45 },
    });

    fire(40, {
      shapes: ['circle'],
      scalar: 2,
      spread: 360,
      startVelocity: 25,
      origin: { x: 0.5, y: 0.5 },
    });

    const tailMs = Math.min(1200, totalDuration * 0.2);
    const emissionMs = Math.max(0, totalDuration - tailMs - FADE_DURATION);

    const emissionEnd = Date.now() + emissionMs;

    const intervalId = window.setInterval(() => {
      if (Date.now() >= emissionEnd) {
        clearInterval(intervalId);
        return;
      }

      const remainingRatio = emissionMs > 0 ? (emissionEnd - Date.now()) / emissionMs : 0;

      const pc = Math.max(10, Math.floor(30 * remainingRatio) + 10);

      fire(pc, {
        angle: 60,
        spread: 55,
        origin: { x: 0, y: Math.random() * 0.4 + 0.3 },
      });

      fire(pc, {
        angle: 120,
        spread: 55,
        origin: { x: 1, y: Math.random() * 0.4 + 0.3 },
      });

      if (Math.random() > 0.3) {
        fire(Math.max(8, Math.floor(pc * 0.6)), {
          angle: 90,
          spread: 100,
          startVelocity: 40,
          origin: {
            x: Math.random() * 0.6 + 0.2,
            y: 0,
          },
        });
      }

      if (Math.random() > 0.65) {
        fire(20, {
          spread: 360,
          startVelocity: 20,
          scalar: 0.9,
          origin: {
            x: Math.random() * 0.4 + 0.3,
            y: Math.random() * 0.3 + 0.3,
          },
        });
      }
    }, 250);

    // Hide title before canvas fades
    const titleHideTimeoutId = window.setTimeout(
      () => {
        setTitleVisible(false);
      },
      Math.max(1000, totalDuration - FADE_DURATION - 1000),
    );

    const fadeStartTime = totalDuration - FADE_DURATION;

    const fadeTimeoutId = window.setTimeout(() => {
      setPhase('fading');
    }, fadeStartTime);

    const removeTimeoutId = setTimeout(() => {
      setVisible(false);
      setPhase('idle');

      // now cleanup after fade completed
      blast.reset();

      onCompleteRef.current?.();
    }, totalDuration + FADE_DURATION);

    const localCleanup = () => {
      clearInterval(intervalId);
      clearTimeout(titleHideTimeoutId);
      clearTimeout(fadeTimeoutId);
      clearTimeout(removeTimeoutId);
      // blast.reset();
    };

    cleanupRef.current = localCleanup;

    return () => {
      localCleanup();

      if (cleanupRef.current === localCleanup) {
        cleanupRef.current = null;
      }
    };
  }, [visible, phase, duration]);

  if (!visible) return null;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 2147483647,
        opacity: phase === 'fading' ? 0 : 1,
        transition: `opacity ${FADE_DURATION}ms ease-in-out`,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          position: 'absolute',
          inset: 0,
          transition: `opacity ${FADE_DURATION}ms ease-in-out`,
        }}
      />

      {title && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            opacity: titleVisible ? 1 : 0,
            transition: `opacity ${TITLE_FADE_DURATION}ms ease`,
          }}
        >
          <h1
            className="rochester-regular"
            style={{
              textTransform: 'capitalize',
              margin: 0,
              color: '#fff',
              // fontSize: "clamp(2.8rem, 6vw, 4rem)",
              fontWeight: 800,
              textAlign: 'center',
              lineHeight: 1.3,
              textShadow: `
                                0 0 10px rgba(255,199,0,0.9),
                                0 0 30px rgba(201, 107, 255, 0.7),
                                0 0 60px rgba(108,92,231,0.5),
                                0 2px 8px rgba(0,0,0,0.6)
                            `,
              animation: titleVisible
                ? 'celebrationTitleEntry 0.8s cubic-bezier(0.34,1.56,0.64,1) both'
                : 'none',
              maxWidth: '90vw',
              wordBreak: 'break-word',
            }}
          >
            {title}
          </h1>
        </div>
      )}

      <style>{`
                @keyframes celebrationTitleEntry {
                    0% {
                        opacity: 0;
                        transform: scale(0.3) translateY(40px);
                        filter: blur(8px);
                    }
                    50% {
                        opacity: 1;
                        filter: blur(0px);
                    }
                    70% {
                        transform: scale(1.08) translateY(-5px);
                    }
                    100% {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                        filter: blur(0px);
                    }
                }
            `}</style>
    </div>
  );
};

export default CelebrationBlast;
